import {
  useBudgetActions,
  useBudgetMeta,
  useBudgetProvider,
  useBudgetProviders,
  useBudgetServicesState,
  useBudgetSuppliersState,
  useBudgetTotals,
} from "../../../../contexts/BudgetContext/projectbudgets.context";
import {
  hasServicesInVersion,
  isCategoryVisibleInVersion,
  isSupplierVisibleInVersion,
} from "../../../../utils/budget-stage-visibility";
import Swal from "sweetalert2";
import {
  AddedTypes,
  Budget,
  BudgetItemType,
  BudgetItemTypeEnum,
  CalcOption,
  initialPaymentSolicitation,
} from "../../../../types/budget/budget";
import BudgetItem from "../BudgetItem";
import CategoryAccordionHeader from "../BudgetItem/CategoryAccordionHeader";
import MoveCategoryBillingModal from "../MoveCategoryBillingModal";
import TransferCategorySupplierModal, {
  TransferTarget,
} from "../TransferCategorySupplierModal";
import {
  isTemporarySupplierOption,
  parseSupplierId,
  resolveNewSupplierId,
  resolveSelectedSupplierId,
} from "../../../../utils/budget-supplier-id";
import { useTransferTargets } from "../hooks/useTransferTargets";
import { shouldUsePreservedTotals } from "../../../../utils/preserved-totals";
import { dispatchAddService } from "../../../../utils/add-service-gesture";
import { Box, Button, Divider, IconButton, Tooltip } from "@mui/material";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MdDragIndicator } from "react-icons/md";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SupplierAutoComplete from "../../SupplierAutoComplete";
import ActionSelect from "../RightSideBar/ActionSelect";
import { toast } from "react-toastify";
import {
  findTransferCategoryBlockReason,
  getSupplierRenameBlockReason,
  hasMoveCategoryDuplicate,
  type TransferCategorySupplierPayload,
  usesStageScopedSupplierCardSwap,
} from "../../../../contexts/BudgetContext/reducer-funcion";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { currencyFormatter } from "../../../../utils/currencyFormatter";
import { BudgetService } from "../../../../services/budget.service";
import {
  sortItemsByCategory,
  sortItemsByCategoryRespectingOrder,
} from "../../../../utils/category-sort";
import { TabActive } from "../../../../types";
import { useProject } from "../../../../contexts/project.context";
import DescriptionModal from "../../../Project/components/DescriptionModal";
import { Descriptions } from "../../../../types/budget/service-item";
import { ReactComponent as DescriptionBlueIcon } from "../../../../assets/styled-icons/DescriptionBlue.svg";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  resolveStageTotalsSource,
  sumSupplierTotals,
} from "../../../../utils/budget-supplier-totals";

type PropTypes = {
  supplierId: string;
  index: number;
  /**
   * Posição visível (0-based) na etapa/forma atuais. O `index` é o absoluto
   * em `supplierList` — o que o reducer precisa — e inclui linhas ocultas.
   * O número na tela usa este, senão um slot fantasma vira nº 11 no retry.
   */
  displayIndex?: number;
  calcOption: string;
  /** Id estável da linha no `SortableContext` de fornecedores (ver ActiveBudget). */
  sortableId?: string;
  /** Etapa somente-leitura: esconde o grip em vez de arrastar sem efeito. */
  dragDisabled?: boolean;
};

/**
 * Linha de categoria arrastável.
 *
 * O grip fica FORA do acordeão de propósito: a categoria é renderizada por dois
 * componentes distintos conforme o estado (`BudgetItem` quando aberta,
 * `CategoryAccordionHeader` quando fechada). Mantendo o grip no wrapper, o drag
 * continua funcionando nos dois casos e nenhum dos dois precisa conhecer o
 * @dnd-kit.
 */
const SortableCategoryRow: React.FC<{
  id: string;
  disabled: boolean;
  children: React.ReactNode;
}> = ({ id, disabled, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id, disabled });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 0.5,
        width: "100%",
        // Linha indicadora do alvo de drop, conforme o cursor passa por cima.
        borderTop:
          isOver && !isDragging ? "2px solid #1361A4" : "2px solid transparent",
      }}
    >
      {!disabled && (
        <Box
          {...attributes}
          {...listeners}
          aria-label="Reordenar categoria"
          sx={{
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
            display: "flex",
            alignItems: "center",
            alignSelf: "stretch",
            padding: "2px",
            "&:hover": { backgroundColor: "#f5f5f5", borderRadius: 1 },
          }}
        >
          <MdDragIndicator size={20} color="#666" />
        </Box>
      )}
      <Box flex={1} minWidth={0}>
        {children}
      </Box>
    </Box>
  );
};

// Movido para `utils/budget-supplier-totals.ts` para que o total do
// AGRUPAMENTO some pelo mesmo caminho que esta linha exibe.
const findAndSumBySupplierId = sumSupplierTotals;

/**
 * Ids dos acordeões derivados do TIPO da categoria (antes vinham da posição na
 * lista). Posição deixou de servir a partir do momento em que o usuário pode
 * reordenar: "panel0" passaria a apontar para outra categoria depois do drag,
 * e o acordeão aberto saltaria para a vizinha.
 */
const getCategoryPanelId = (type: string) => `panel-${type}`;
const getCategoryBaseValuePanelId = (type: string) => `budgetPanel-${type}`;

const BudgetSupplier: React.FC<PropTypes> = ({
  supplierId,
  index,
  displayIndex,
  calcOption,
  sortableId,
  dragDisabled,
}: PropTypes) => {
  // Subscribe to the smallest slices the row actually reads. Splitting the
  // monolithic `useBudget()` hook here means a keystroke in any service
  // input — which only invalidates the Services slice — no longer schedules
  // a re-render for every supplier row in the budget.
  const { activeBudgetDispatch, runBudgetAutosave } = useBudgetActions();
  const { activeCalcOption, versionInfo, isBudgetDisabled, activeBudgetId } =
    useBudgetMeta();

  // Drag da própria linha de fornecedor. O `SortableContext` que a governa vive
  // em `ActiveBudget/SupplierRows`, que também converte a posição visível em
  // índice absoluto de `supplierList`.
  const supplierDragDisabled = !!dragDisabled || isBudgetDisabled;
  const {
    attributes: supplierDragAttributes,
    listeners: supplierDragListeners,
    setNodeRef: setSupplierDragNodeRef,
    transform: supplierDragTransform,
    transition: supplierDragTransition,
    isDragging: isSupplierDragging,
    isOver: isSupplierDropTarget,
  } = useSortable({
    id: sortableId ?? supplierId,
    disabled: supplierDragDisabled,
  });

  // Mesmo `activationConstraint` do drag de fornecedores: exigir 8px de
  // movimento é o que separa "clicar no botão da categoria" de "arrastar".
  const categorySensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const { supplierList, addedItemTypeList, paymentSolicitation, itemTypeList } =
    useBudgetSuppliersState();
  const { budgetServices } = useBudgetServicesState();
  const { budgetValuesTotal, negociationValuesTotal, closingValuesTotal } =
    useBudgetTotals();

  // Per-row refs: preserve totals/savings when swapping supplier on THIS row
  // without leaking values across BudgetSupplier instances (module Maps did).
  const previousTotalsRef = useRef<Array<{
    title: string;
    value: number;
  }> | null>(null);
  const previousSavingsRef = useRef<Array<{
    title: string;
    value: number;
  }> | null>(null);

  // const [supplierIdCleaned, setSupplierIdCleaned] = useState<string>(``);

  // useEffect(() => {
  //   const supplierIdSplit = supplierId.split("-");
  //   if (supplierIdSplit.length > 1) {
  //     setSupplierIdCleaned(supplierIdSplit[0]);
  //   } else {
  //     setSupplierIdCleaned("");
  //   }
  // }, [supplierId]);

  const parsedSupplierId = useMemo(() => {
    if (supplierId.includes("|")) return supplierId.split("|")[0];
    const parts = supplierId.split("-");
    return parts.length > 1 ? parts[0] : supplierId;
  }, [supplierId]);

  const { tabActive, project } = useProject();
  const [accordionState, setAccordionState] = useState<Map<string, boolean>>(
    new Map()
  );
  const [baseValuesAccordionState, setBaseValuesAccordionState] = useState<
    Map<string, boolean>
  >(new Map());
  const categoryAccordionSwitchTimeoutRef = useRef<number | null>(null);
  const baseValueAccordionSwitchTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (categoryAccordionSwitchTimeoutRef.current) {
        window.clearTimeout(categoryAccordionSwitchTimeoutRef.current);
      }
      if (baseValueAccordionSwitchTimeoutRef.current) {
        window.clearTimeout(baseValueAccordionSwitchTimeoutRef.current);
      }
    };
  }, []);

  // Categoria alvo de cada modal do botão "Mover" (null = modal fechado).
  const [moveModalType, setMoveModalType] = useState<BudgetItemType | null>(
    null
  );
  const [transferModalType, setTransferModalType] =
    useState<BudgetItemType | null>(null);

  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [descriptions, setDescriptions] = useState<Descriptions>({
    composition: "",
    cancelPolicy: "",
    paymentMethod: "",
    commercialConditions: false,
  });

  // Load descriptions from supplier when component mounts
  useEffect(() => {
    const supplier = supplierList?.find(
      (s) => s.supplierId === supplierId && s.calcOption === activeCalcOption
    );
    if (supplier?.descriptions) {
      setDescriptions(supplier.descriptions);
    } else {
      setDescriptions({
        composition: "",
        cancelPolicy: "",
        paymentMethod: "",
        commercialConditions: false,
      });
    }
  }, [supplierId, activeCalcOption, supplierList]);

  // Calcula totais reais do fornecedor atual (usado para detectar se há valores)
  const actualTotals = useMemo(() => {
    const totalsSource = resolveStageTotalsSource({
      budgetValuesTotal,
      negociationValuesTotal,
      closingValuesTotal,
      versionInfo,
      calcOption,
    });

    const { sumTotals: totals } = findAndSumBySupplierId(
      totalsSource,
      supplierId,
      {
        version: versionInfo?.version ?? 0,
        status: versionInfo?.status,
        supplierList,
        budgetServices,
        addedItemTypeList,
      }
    );

    return totals;
  }, [
    budgetValuesTotal,
    negociationValuesTotal,
    closingValuesTotal,
    versionInfo,
    calcOption,
    supplierId,
    supplierList,
    budgetServices,
    addedItemTypeList,
  ]);

  /**
   * O fornecedor ainda tem algum SERVIÇO nesta forma de cobrança e nesta etapa?
   *
   * Enquanto o usuário troca o fornecedor da linha, os totais passam por um
   * instante zerados e os refs abaixo seguram o último valor válido para evitar
   * piscada. Só que "zero" também é um resultado LEGÍTIMO — é o que sobra
   * depois de mover/transferir a última categoria, ou o último serviço, do
   * fornecedor. Sem esta checagem o header continuava exibindo o valor que já
   * saiu (fornecedor zerado e header em R$ 89.102,40).
   *
   * A posse é medida em SERVIÇO, não em entrada de `addedItemTypeList`: a
   * transferência de serviço deixa a categoria vazia na origem de propósito, e
   * contar categorias fazia o zero real passar por transitório — ver
   * `shouldUsePreservedTotals`.
   */
  const hasServicesInCalcOption = useMemo(
    () =>
      hasServicesInVersion(
        budgetServices,
        supplierId,
        activeCalcOption,
        versionInfo?.version ?? 0,
        undefined,
        versionInfo?.status
      ),
    [
      budgetServices,
      supplierId,
      activeCalcOption,
      versionInfo?.version,
      versionInfo?.status,
    ]
  );

  // Totais preservados para exibição (mantém valores ao trocar fornecedor nesta linha)
  const totalsSummary = useMemo(() => {
    const newTotals = [
      { title: "T. Tarifas", value: actualTotals.tariffTotals },
      { title: "T. Taxas", value: actualTotals.serviceTaxTotals },
      { title: "T. Receita", value: actualTotals.revenueTotals },
      { title: "T. Imposto", value: actualTotals.taxes },
      { title: "T. Geral", value: actualTotals.totals },
    ];

    const preserve = shouldUsePreservedTotals({
      currentTotal: actualTotals.totals,
      ownsContent: hasServicesInCalcOption,
      hasPreserved: previousTotalsRef.current != null,
    });

    if (preserve) return previousTotalsRef.current!;

    previousTotalsRef.current = actualTotals.totals > 0 ? newTotals : null;
    return newTotals;
  }, [actualTotals, hasServicesInCalcOption]);

  // Economias preservadas (mantém valores ao trocar fornecedor nesta linha)
  const savingsSummary = useMemo(() => {
    const currentTotals = totalsSummary.reduce((acc, item) => {
      acc[item.title] = item.value;
      return acc;
    }, {} as Record<string, number>);

    // Determine comparison totals source based on comparisonVersion
    let comparisonTotalsSource: any;
    if (versionInfo?.comparisonVersion === 0) {
      comparisonTotalsSource = (budgetValuesTotal?.byCalculationType as any)?.[
        calcOption
      ];
    } else if (versionInfo?.comparisonVersion > 0) {
      comparisonTotalsSource = (
        negociationValuesTotal?.[versionInfo.comparisonVersion - 1]
          ?.byCalculationType as any
      )?.[calcOption];
    } else {
      // Fallback to budgetValuesTotal if comparisonVersion is not set
      comparisonTotalsSource = (budgetValuesTotal?.byCalculationType as any)?.[
        calcOption
      ];
    }

    const { sumTotals: comparisonTotals } = findAndSumBySupplierId(
      comparisonTotalsSource,
      supplierId
    );

    const newSavings = [
      {
        title: "T. Tarifas",
        value:
          currentTotals["T. Tarifas"] - (comparisonTotals.tariffTotals || 0),
      },
      {
        title: "T. Taxas",
        value:
          currentTotals["T. Taxas"] - (comparisonTotals.serviceTaxTotals || 0),
      },
      {
        title: "T. Receita",
        value:
          currentTotals["T. Receita"] - (comparisonTotals.revenueTotals || 0),
      },
      {
        title: "T. Imposto",
        value: currentTotals["T. Imposto"] - (comparisonTotals.taxes || 0),
      },
      {
        title: "T. Geral",
        value: currentTotals["T. Geral"] - (comparisonTotals.totals || 0),
      },
    ];

    const preserve = shouldUsePreservedTotals({
      currentTotal: actualTotals.totals,
      ownsContent: hasServicesInCalcOption,
      hasPreserved: previousSavingsRef.current != null,
    });

    if (preserve) return previousSavingsRef.current!;

    previousSavingsRef.current = actualTotals.totals > 0 ? newSavings : null;
    return newSavings;
  }, [
    hasServicesInCalcOption,
    totalsSummary,
    actualTotals,
    budgetValuesTotal,
    negociationValuesTotal,
    versionInfo,
    calcOption,
    supplierId,
  ]);

  // Resolved supplier metadata. Lifted out of this component into
  // `BudgetProvider`, which runs a single `useQueries` over the unique
  // supplier ids of the active budget. With many suppliers we now mount
  // one provider hook (in the parent) instead of one per row, and
  // every row reads from the same shared map via context.
  const provider = useBudgetProvider(supplierId);
  // Mapa compartilhado de fornecedores resolvidos: usado para rotular os
  // destinos possíveis da transferência sem disparar uma query por linha.
  const { providersById } = useBudgetProviders();

  // Ocorrências do MESMO (fornecedor, bloco) na `supplierList`, em duas leituras:
  //
  // `all`     → governa a necessidade de um id próprio para esta linha. Duas entradas
  //             com o id idêntico disputam a mesma identidade (visibilidade por etapa,
  //             serviços, totais), então a segunda sempre precisa do sufixo `|dup|`.
  // `visible` → governa o AVISO de duplicidade. A `supplierList` não é versionada: o
  //             fornecedor removido numa negociação/fechamento continua na lista, apenas
  //             oculto na tela (ver SupplierRows em ActiveBudget). Acusar essa entrada
  //             como duplicata apontava para "a linha N" que o usuário não vê e não tem
  //             como corrigir — sintoma relatado ao re-adicionar no fechamento um
  //             fornecedor removido na negociação.
  const sameSupplierOccurrences = useMemo(() => {
    if (!parsedSupplierId || !supplierList) return null;
    if (supplierId.includes("-")) return null;

    const all = supplierList
      .map((el, idx) => {
        const parsed = el.supplierId.includes("|")
          ? el.supplierId.split("|")[0]
          : el.supplierId.includes("-")
          ? el.supplierId.split("-")[0]
          : el.supplierId;
        return { ...el, originalIndex: idx, parsedId: parsed };
      })
      .filter(
        (el) =>
          el.calcOption === activeCalcOption &&
          el.parsedId === parsedSupplierId &&
          !!el.parsedId &&
          !el.supplierId.includes("-")
      );

    const visible = all.filter((el) =>
      isSupplierVisibleInVersion(
        budgetServices,
        el.supplierId,
        activeCalcOption,
        versionInfo?.version ?? 0,
        el.deletedInVersions,
        versionInfo?.status
      )
    );

    return { all, visible };
  }, [
    supplierList,
    budgetServices,
    activeCalcOption,
    parsedSupplierId,
    supplierId,
    versionInfo?.version,
  ]);

  /** Existe outra entrada do mesmo fornecedor ANTES desta: esta precisa de id próprio. */
  const needsDistinctId = useMemo(() => {
    const all = sameSupplierOccurrences?.all;
    if (!all || all.length <= 1) return false;
    return all[0].originalIndex !== index;
  }, [sameSupplierOccurrences, index]);

  const duplicateInfo = useMemo(() => {
    const visible = sameSupplierOccurrences?.visible;
    if (!visible || visible.length <= 1) return null;

    const firstOccurrence = visible[0];
    if (firstOccurrence.originalIndex === index) return null;

    return {
      isDuplicate: true,
      originalLineNumber: firstOccurrence.originalIndex + 1,
    };
  }, [sameSupplierOccurrences, index]);

  useEffect(() => {
    // Desacoplado de `duplicateInfo`: mesmo sem aviso na tela (a outra entrada está
    // oculta nesta etapa) a linha precisa de id próprio, senão ela herda a
    // visibilidade da entrada removida e some assim que o fornecedor é selecionado.
    if (!needsDistinctId) return;
    if (!parsedSupplierId) return;
    if (supplierId.includes("|dup|")) return;
    if (isBudgetDisabled) return;

    const sameIdCount =
      supplierList?.filter((el) => {
        const base = el.supplierId.includes("|")
          ? el.supplierId.split("|")[0]
          : el.supplierId.includes("-")
          ? el.supplierId.split("-")[0]
          : el.supplierId;
        return base === parsedSupplierId && el.calcOption === activeCalcOption;
      }).length ?? 0;

    activeBudgetDispatch({
      type: "UPDATE_SUPPLIER_LIST",
      payload: {
        currentId: supplierId,
        newId: parsedSupplierId + `|dup|${sameIdCount}`,
        newName: "",
        calcOption: activeCalcOption,
        supplierListIndex: index,
        silent: true,
      },
      versionInfo,
    });
  }, [supplierId, needsDistinctId]);

  useEffect(() => {
    if (!supplierId || !supplierId.includes("-")) return;
    if (isBudgetDisabled) return;

    const hasDuplicateTempId = supplierList?.some(
      (el, idx) =>
        idx !== index &&
        el.supplierId === supplierId &&
        el.calcOption === activeCalcOption
    );

    if (!hasDuplicateTempId) return;

    const existingTempSuffixes = (supplierList ?? [])
      .filter((el, idx) => {
        if (idx === index) return false;
        const base = el.supplierId.includes("|")
          ? el.supplierId.split("|")[0]
          : el.supplierId.split("-")[0];
        return base === parsedSupplierId && el.calcOption === activeCalcOption;
      })
      .map((el) => {
        const parts = el.supplierId.split("-");
        const n = parseInt(parts[parts.length - 1], 10);
        return isNaN(n) ? -1 : n;
      })
      .filter((n) => n >= 0);

    const nextSuffix =
      existingTempSuffixes.length === 0
        ? 0
        : Math.max(...existingTempSuffixes) + 1;

    activeBudgetDispatch({
      type: "UPDATE_SUPPLIER_LIST",
      payload: {
        currentId: supplierId,
        newId: `${parsedSupplierId}-${activeCalcOption}-${nextSuffix}`,
        newName: "",
        calcOption: activeCalcOption,
        supplierListIndex: index,
        silent: true,
      },
      versionInfo,
    });
  }, [supplierId]);

  type Option = { label: string; value: string };

  // Função para gerar as opções
  function generateFilteredOptions(
    enumObj: typeof BudgetItemTypeEnum,
    itemTypeList: { [key in keyof typeof BudgetItemTypeEnum]?: boolean }
  ): Option[] {
    if (!itemTypeList) {
      return [];
    }
    const keys = Object.keys(enumObj) as (keyof typeof BudgetItemTypeEnum)[];
    const filtered = keys.filter((key) => itemTypeList[key] === true);
    const options = filtered.map((key) => ({
      label: enumObj[key],
      value: String(key),
      type: String(key),
    }));
    return sortItemsByCategory(options, "type").map(({ label, value }) => ({
      label,
      value,
    }));
  }

  function handleStateChange(value: any, name: any): boolean | void {
    if (!value) return;

    // O id próprio é decidido AQUI, na seleção — não num efeito pós-render.
    //
    // Se a linha ficasse, ainda que por um único render, com o id de um fornecedor que
    // já existe na lista e não tem serviço nesta versão, o `SupplierRows` a esconderia
    // (visibilidade por etapa) e desmontaria este componente — e o efeito que aplicaria
    // o sufixo nunca chegaria a rodar. O fornecedor recém-escolhido simplesmente sumia.
    // É o caso de re-adicionar no fechamento um fornecedor removido na negociação.
    //
    // O autocomplete já pode ter resolvido o id (`|dup|` / `-<forma>-n`).
    // Reparseia o `_id` real e resolve de novo: comparar `base === value`
    // falhava quando `value` já vinha sufixado, e duas linhas herdavam o
    // mesmo id.
    const realId = parseSupplierId(value) || value;
    const isTemporary =
      isTemporarySupplierOption({ name }) ||
      (typeof value === "string" &&
        value.includes(`-${activeCalcOption}-`) &&
        !value.includes("|dup|"));
    const newId = resolveSelectedSupplierId({
      realSupplierId: realId,
      calcOption: activeCalcOption,
      supplierList,
      currentSupplierId: supplierId,
      isTemporary,
    });

    const blockReason = getSupplierRenameBlockReason(
      { supplierList, budgetServices, addedItemTypeList },
      {
        currentId: supplierId,
        newId,
        calcOption: activeCalcOption,
        supplierListIndex: index,
      },
      versionInfo
    );
    if (blockReason) {
      toast.error(blockReason);
      return false;
    }

    // Sem `versionInfo` o reducer recalcula como orçamento (v0): no
    // fechamento/negociação a árvore da etapa ficava com o id antigo e o card
    // mostrava R$ 0,00 até a próxima edição de serviço (MMV-2626).
    activeBudgetDispatch({
      type: "UPDATE_SUPPLIER_LIST",
      payload: {
        currentId: supplierId,
        newId,
        newName: name,
        calcOption: activeCalcOption,
        supplierListIndex: index,
        ...(usesStageScopedSupplierCardSwap(
          { supplierList, budgetServices },
          {
            currentId: supplierId,
            newId,
            calcOption: activeCalcOption,
            supplierListIndex: index,
          },
          versionInfo
        )
          ? { stageScopedCardSwap: true }
          : {}),
      },
      versionInfo,
    });
  }

  // `addedItemTypeList` é compartilhado por todas as etapas: a categoria só
  // aparece nesta etapa se tiver algum serviço na versão atual (ver
  // DELETE_BUDGET_ITEM em BudgetContext/reducer-funcion.ts).
  function isCategoryVisibleHere(category: AddedTypes) {
    return isCategoryVisibleInVersion(
      budgetServices,
      supplierId,
      category.type,
      activeCalcOption,
      versionInfo?.version ?? 0,
      category.deletedInVersions,
      versionInfo?.status
    );
  }

  function addOption(optionValue: string) {
    // MMV-2643: só bloqueia a categoria que está NA TELA. A que foi excluída
    // (ou movida para outra forma) nesta etapa continua na lista, escondida — e
    // bloqueá-la tornava impossível incluí-la de novo.
    const hasItemAlready = addedItemTypeList?.some(
      (el) =>
        el.supplierId === supplierId &&
        el.type === optionValue &&
        el.calcOption === activeCalcOption &&
        isCategoryVisibleHere(el)
    );
    if (hasItemAlready)
      return toast.warn("A opção selecionada já existe para esse fornecedor");
    if (!supplierId)
      return toast.warn("Selecione um fornecedor para adicionar serviços.");

    activeBudgetDispatch({
      type: "ADD_BUDGET_ITEM",
      payload: { type: optionValue, supplierId, calcOption: activeCalcOption },
      versionInfo,
    });

    dispatchAddService(activeBudgetDispatch, {
      type: "ADD_SERVICE",
      payload: {
        supplierId,
        supplierName: provider?.name || "",
        type: optionValue,
        tabActive,
        calcOption: activeCalcOption,
        versionInfo,
        projectEvent: project?.event,
      },
    });

    // Set the newly added item's accordion to expanded and collapse all others
    setAccordionState(() => {
      const newState = new Map(); // Fresh state - all others collapsed
      newState.set(getCategoryPanelId(optionValue), true); // Only expand the new item
      return newState;
    });
  }
  const isExpanded = (panel: string) => {
    return accordionState.get(panel) || false;
  };

  const isBaseValueExpanded = (panel: string) => {
    return baseValuesAccordionState.get(panel) || false;
  };

  const switchExclusiveAccordion = useCallback(
    (
      panel: string,
      isExpanded: boolean,
      setState: React.Dispatch<React.SetStateAction<Map<string, boolean>>>,
      switchTimeoutRef: React.MutableRefObject<number | null>,
      collapseDelayMs = 280
    ) => {
      if (switchTimeoutRef.current) {
        window.clearTimeout(switchTimeoutRef.current);
        switchTimeoutRef.current = null;
      }

      if (!isExpanded) {
        setState(new Map());
        return;
      }

      setState((prev) => {
        const openPanel = Array.from(prev.entries()).find(
          ([, value]) => value
        )?.[0];
        if (openPanel && openPanel !== panel) {
          switchTimeoutRef.current = window.setTimeout(() => {
            setState(new Map([[panel, true]]));
            switchTimeoutRef.current = null;
          }, collapseDelayMs);
          return new Map();
        }
        return new Map([[panel, true]]);
      });
    },
    []
  );

  const handleChangeBaseValue = useCallback(
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      switchExclusiveAccordion(
        panel,
        isExpanded,
        setBaseValuesAccordionState,
        baseValueAccordionSwitchTimeoutRef
      );
    },
    [switchExclusiveAccordion]
  );

  // `handleChange` is passed down to `BudgetItem`. Memoizing it lets the
  // memoized `BudgetItem` skip re-render whenever an unrelated slice (e.g.
  // services / totals / providers) changes. The setter from `useState` is
  // stable, so an empty dep list is correct.
  const handleChange = useCallback(
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      switchExclusiveAccordion(
        panel,
        isExpanded,
        setAccordionState,
        categoryAccordionSwitchTimeoutRef
      );
    },
    [switchExclusiveAccordion]
  );
  function renderActiveOptions() {
    // Filtra ANTES de ordenar: a ordem escolhida pelo usuário vale dentro de um
    // fornecedor, e `addedItemTypeList` é uma lista plana com as categorias de
    // todos eles.
    const supplierCategories = (addedItemTypeList || []).filter(
      (el) =>
        el.supplierId === supplierId &&
        el.calcOption === activeCalcOption &&
        isCategoryVisibleHere(el)
    );

    // Enquanto ninguém arrastar, `order` é indefinido e isto se comporta
    // exatamente como o antigo `sortItemsByCategory` (ordem fixa de categorias).
    const sortedItems = sortItemsByCategoryRespectingOrder(
      supplierCategories,
      "type"
    );

    const categoryIds = sortedItems.map((el) => el.type);

    const handleCategoryDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      // Soltou fora da lista de categorias deste fornecedor: `over` vem nulo e
      // a ordem não muda.
      if (!over || active.id === over.id) return;

      const oldIndex = categoryIds.indexOf(active.id as BudgetItemType);
      const newIndex = categoryIds.indexOf(over.id as BudgetItemType);
      if (oldIndex < 0 || newIndex < 0) return;

      const orderedTypes = [...categoryIds];
      const [movedType] = orderedTypes.splice(oldIndex, 1);
      orderedTypes.splice(newIndex, 0, movedType);

      activeBudgetDispatch({
        type: "REORDER_CATEGORIES",
        payload: {
          supplierId,
          calcOption: activeCalcOption,
          orderedTypes,
        },
      });

      // O dispatch sozinho não faz a troca de aba dar flush (ver comentário em
      // BudgetItem/handleDragEnd). Sem isto a nova ordem se perderia ao sair da
      // aba antes do autosave de intervalo.
      if (activeBudgetId) {
        window.setTimeout(() => {
          void runBudgetAutosave("manual");
        }, 0);
      }
    };

    return (
      <DndContext
        sensors={categorySensors}
        collisionDetection={closestCenter}
        onDragEnd={handleCategoryDragEnd}
      >
        <SortableContext
          items={categoryIds}
          strategy={verticalListSortingStrategy}
        >
          {sortedItems.map((el, index) => {
            const savedPaymentInfo =
              paymentSolicitation?.find(
                (paymentInfo) =>
                  paymentInfo.type === el.type &&
                  paymentInfo.supplierId === supplierId &&
                  paymentInfo.calcOption === activeCalcOption
              ) || initialPaymentSolicitation;
            // Painel identificado pelo TIPO da categoria, não pela posição: o
            // acordeão aberto continua aberto depois de uma reordenação, e o
            // estado não "pula" para a categoria vizinha.
            const panel = getCategoryPanelId(el.type);
            const baseValuePanel = getCategoryBaseValuePanelId(el.type);
            const categoryExpanded = isExpanded(panel);

            return (
              <SortableCategoryRow
                key={`${el.type}-${el.calcOption}-${el.supplierId}`}
                id={el.type}
                disabled={isBudgetDisabled}
              >
                {categoryExpanded ? (
                  <BudgetItem
                    type={el.type}
                    supplierId={supplierId}
                    supplierName={provider?.name || ""}
                    paymentInfo={savedPaymentInfo}
                    expanded={categoryExpanded}
                    baseValueExpanded={isBaseValueExpanded(baseValuePanel)}
                    onChange={handleChange}
                    panel={panel}
                    baseValuePanel={baseValuePanel}
                    onRemoveItem={removeBudgetItem}
                    onMoveBillingModel={openMoveBillingModal}
                    onTransferSupplier={openTransferModal}
                    index={index}
                    onChangeBaseValue={handleChangeBaseValue}
                  />
                ) : (
                  <CategoryAccordionHeader
                    type={el.type}
                    supplierId={supplierId}
                    panel={panel}
                    index={index}
                    onChange={handleChange}
                    onMoveBillingModel={openMoveBillingModal}
                    onTransferSupplier={openTransferModal}
                    onRemoveItem={removeBudgetItem}
                  />
                )}
              </SortableCategoryRow>
            );
          })}
        </SortableContext>
      </DndContext>
    );
  }
  // `removeBudgetItem` and the modal openers are also forwarded to
  // `BudgetItem` as props, so they must be stable to make `React.memo` on
  // `BudgetItem` effective. `activeBudgetDispatch` and `runBudgetAutosave`
  // come from the ref-stable Actions slice, so they don't belong in the dep
  // arrays — only the closed-over data values do.
  const removeBudgetItem = useCallback(
    (type: BudgetItemType) => {
      Swal.fire({
        title: "Tem certeza?",
        text: "Ao apagar este item todos serviços relacionados também serão apagados!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Não, cancelar",
        confirmButtonText: "Sim, prossiga",
      }).then((result) => {
        if (result.isConfirmed) {
          // Só o reducer altera o orçamento; SAVE antes do recálculo persistia
          // budgetValuesTotal desatualizado (mesmo problema do DELETE_SERVICE).
          activeBudgetDispatch({
            type: "DELETE_BUDGET_ITEM",
            payload: {
              type,
              supplierId,
              calcOption: activeCalcOption,
            },
            versionInfo,
          });
          if (activeBudgetId) {
            window.setTimeout(() => {
              void runBudgetAutosave("manual");
            }, 0);
          }
          Swal.fire({
            title: "Apagado",
            text: "O item foi apagado",
            icon: "success",
          });
        }
      });
    },
    [
      activeBudgetDispatch,
      runBudgetAutosave,
      supplierId,
      activeCalcOption,
      versionInfo,
      activeBudgetId,
    ]
  );

  /**
   * Aplica a movimentação de forma de cobrança já confirmada no modal.
   * Resolve `true` só quando o estado foi alterado E persistido — o modal usa
   * isso para decidir entre avançar para o passo de sucesso ou deixar o usuário
   * tentar de novo.
   */
  const confirmMoveCategory = useCallback(
    async (
      type: BudgetItemType,
      targetCalcOption: CalcOption
    ): Promise<boolean> => {
      const fromOption = activeCalcOption as CalcOption;
      if (fromOption === targetCalcOption) return false;

      const categoryLabel =
        BudgetItemTypeEnum[type as keyof typeof BudgetItemTypeEnum];

      // O modelo não suporta a mesma (fornecedor, categoria, forma de cobrança)
      // duas vezes: a de destino sobrescreveria a existente.
      const hasDuplicate = hasMoveCategoryDuplicate(
        { addedItemTypeList, budgetServices },
        { type, supplierId, toCalcOption: targetCalcOption },
        versionInfo
      );

      if (hasDuplicate) {
        toast.error(
          `Já existe a categoria ${categoryLabel} em ${targetCalcOption}`
        );
        return false;
      }

      activeBudgetDispatch({
        type: "MOVE_CATEGORY",
        payload: {
          type,
          typeName: categoryLabel,
          supplierId,
          fromCalcOption: fromOption,
          toCalcOption: targetCalcOption,
        },
        versionInfo,
      });

      if (!activeBudgetId) return true;

      // O dispatch só é aplicado no próximo commit; o autosave lê o orçamento
      // de `latestActiveBudgetRef`, que ainda não foi atualizado neste tick.
      return new Promise<boolean>((resolve) => {
        window.setTimeout(() => {
          void (async () => {
            const ok = await runBudgetAutosave("manual", {
              actionType: "MOVE_CATEGORY",
              auditContext: {
                "Categoria movida": categoryLabel,
                Fornecedor: provider?.name || supplierId,
                "Forma de cobrança": `${fromOption} → ${targetCalcOption}`,
              },
            });
            if (!ok) {
              toast.error(
                "Não foi possível salvar após mover a categoria. Verifique sua conexão e tente salvar de novo."
              );
            }
            resolve(ok);
          })();
        }, 0);
      });
    },
    [
      activeBudgetDispatch,
      runBudgetAutosave,
      supplierId,
      activeCalcOption,
      versionInfo,
      activeBudgetId,
      addedItemTypeList,
      budgetServices,
      provider?.name,
    ]
  );

  /** Fornecedores que podem receber a categoria (ver `useTransferTargets`). */
  const transferTargets = useTransferTargets(activeCalcOption, supplierId);

  /** Aplica a transferência de categoria já confirmada no modal. */
  const confirmTransferCategory = useCallback(
    async (type: BudgetItemType, target: TransferTarget): Promise<boolean> => {
      const categoryLabel =
        BudgetItemTypeEnum[type as keyof typeof BudgetItemTypeEnum];

      // Um fornecedor novo que já ocupe um id nesta forma de cobrança precisa
      // de id próprio, senão as duas linhas disputam a mesma identidade.
      const toSupplierId = target.isNew
        ? resolveNewSupplierId(
            target.realSupplierId,
            activeCalcOption,
            supplierList,
            { isTemporary: target.isTemporary }
          )
        : target.existingSupplierId ?? target.realSupplierId;

      if (!toSupplierId || toSupplierId === supplierId) return false;

      const hasDuplicate = addedItemTypeList?.some(
        (el) =>
          el.type === type &&
          el.supplierId === toSupplierId &&
          el.calcOption === activeCalcOption
      );

      if (hasDuplicate) {
        toast.error(
          `O fornecedor de destino já possui a categoria ${categoryLabel} nesta forma de cobrança.`
        );
        return false;
      }

      const transferPayload: TransferCategorySupplierPayload = {
        type,
        typeName: categoryLabel,
        calcOption: activeCalcOption as CalcOption,
        fromSupplierId: supplierId,
        toSupplierId,
        toSupplierName: target.supplierName,
        createTargetSupplier: target.isNew,
      };

      const blockReason = findTransferCategoryBlockReason(
        { addedItemTypeList, budgetServices } as Budget,
        transferPayload,
        versionInfo
      );
      if (blockReason) {
        toast.error(blockReason);
        return false;
      }

      activeBudgetDispatch({
        type: "TRANSFER_CATEGORY_SUPPLIER",
        payload: transferPayload,
        versionInfo,
      });

      if (!activeBudgetId) return true;

      return new Promise<boolean>((resolve) => {
        window.setTimeout(() => {
          void (async () => {
            const ok = await runBudgetAutosave("manual", {
              actionType: "TRANSFER_CATEGORY",
              auditContext: {
                "Categoria transferida": categoryLabel,
                "Fornecedor de origem": provider?.name || supplierId,
                "Fornecedor de destino": target.supplierName,
                "Forma de cobrança": activeCalcOption,
              },
            });
            if (!ok) {
              toast.error(
                "Não foi possível salvar após transferir a categoria. Verifique sua conexão e tente salvar de novo."
              );
            }
            resolve(ok);
          })();
        }, 0);
      });
    },
    [
      activeBudgetDispatch,
      runBudgetAutosave,
      supplierId,
      activeCalcOption,
      versionInfo,
      activeBudgetId,
      addedItemTypeList,
      budgetServices,
      supplierList,
      provider?.name,
    ]
  );

  const openMoveBillingModal = useCallback((type: BudgetItemType) => {
    setMoveModalType(type);
  }, []);

  const openTransferModal = useCallback((type: BudgetItemType) => {
    setTransferModalType(type);
  }, []);

  function removeSupplier() {
    Swal.fire({
      title: "Tem certeza?",
      text: "Ao apagar este fornecedor todos os dados relacionados também serão apagados!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Não, cancelar",
      confirmButtonText: "Sim, prossiga",
    }).then((result) => {
      if (result.isConfirmed) {
        activeBudgetDispatch({
          type: "DELETE_SUPPLIER",
          payload: {
            supplierId,
            calcOption: activeCalcOption,
            supplierListIndex: index,
          },
          versionInfo: versionInfo,
        });
        if (activeBudgetId) {
          window.setTimeout(() => {
            void runBudgetAutosave("manual");
          }, 0);
        }
        Swal.fire({
          title: "Apagado",
          text: "O item foi apagado",
          icon: "success",
        });
      }
    });
  }

  const handleDescriptionChange = (updatedDescriptions: Descriptions) => {
    setDescriptions(updatedDescriptions);
  };

  const handleDescriptionConfirm = () => {
    activeBudgetDispatch({
      type: "UPDATE_SUPPLIER_DESCRIPTIONS",
      payload: {
        supplierId,
        calcOption: activeCalcOption,
        descriptions,
      },
    });
    if (activeBudgetId) {
      window.setTimeout(() => {
        void runBudgetAutosave("manual");
      }, 0);
    }

    setDescriptionModalOpen(false);
    toast.success("Descritivos salvos com sucesso!");
  };
  return (
    <Box
      ref={setSupplierDragNodeRef}
      style={{
        transform: CSS.Transform.toString(supplierDragTransform),
        transition: supplierDragTransition,
        opacity: isSupplierDragging ? 0.5 : 1,
      }}
      sx={{
        background: duplicateInfo?.isDuplicate ? "#FDE8E8" : "#E9E9EA",
        borderRadius: "8px",
        padding: "15px 15px 6px 15px",
        display: "flex",
        flexDirection: "column",
        margin: "auto",
        width: "100%",
        marginTop: "5px",
        position: "relative",
        // O aviso de duplicidade tem prioridade sobre o destaque de drop target.
        border: duplicateInfo?.isDuplicate
          ? "2px solid #DC2626"
          : isSupplierDropTarget && !isSupplierDragging
          ? "2px dashed #1361A4"
          : "2px solid transparent",
      }}
    >
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        bgcolor="#FFF"
        p={1}
        borderRadius={2}
      >
        <Box
          display={"flex"}
          flex={1}
          margin={"auto"}
          alignItems={"flex-start"}
          gap={1}
        >
          <Box display="flex" alignItems="center" gap={0.5} mt={1}>
            {!supplierDragDisabled && (
              <Box
                {...supplierDragAttributes}
                {...supplierDragListeners}
                aria-label="Reordenar fornecedor"
                sx={{
                  cursor: "grab",
                  "&:active": { cursor: "grabbing" },
                  display: "flex",
                  alignItems: "center",
                  padding: "2px",
                  "&:hover": { backgroundColor: "#f5f5f5", borderRadius: 1 },
                }}
              >
                <MdDragIndicator size={20} color="#666" />
              </Box>
            )}
            <span>{(displayIndex ?? index) + 1}</span>
          </Box>
          <Box display="flex" flexDirection="column" flex={1}>
            <SupplierAutoComplete
              onSelectSupplier={handleStateChange}
              provider_id={supplierId}
              title={``}
              disabled={isBudgetDisabled}
              isBudget={true}
              error={!!duplicateInfo?.isDuplicate}
            />
            {duplicateInfo?.isDuplicate && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mt: 1,
                  p: 1.5,
                  backgroundColor: "#FEE2E2",
                  borderRadius: 1,
                }}
              >
                <ErrorOutlineIcon
                  sx={{ color: "#DC2626", fontSize: 20, mt: "2px" }}
                />
                <Box>
                  <span
                    style={{
                      color: "#DC2626",
                      fontWeight: 600,
                      fontSize: 14,
                      display: "block",
                    }}
                  >
                    Fornecedor duplicado na mesma forma de pagamento
                  </span>
                  <span
                    style={{
                      color: "#666",
                      fontSize: 13,
                      display: "block",
                      marginTop: 2,
                    }}
                  >
                    Este fornecedor ja esta sendo utilizado na linha{" "}
                    {duplicateInfo.originalLineNumber}. Selecione outro
                    fornecedor ou remova o existente.
                  </span>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          style={{ display: "flex", flexDirection: "row" }}
          flex={1}
          height={"100%"}
          justifyContent={"flex-end"}
          gap={1}
        >
          <Divider orientation="vertical" flexItem sx={{ mt: 1 }} />

          <Box
            display="flex"
            flex={0.9}
            justifyContent={"space-evenly"}
            gap={1}
            mt={1}
          >
            {totalsSummary.map((i, index) => {
              const savingsItem = savingsSummary[index];
              return (
                <Box
                  key={index}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                  flex={1}
                >
                  <span
                    style={{
                      color: "rgba(1,1,1,0.5)",
                      fontSize: 14,
                    }}
                  >
                    {i.title}
                  </span>
                  <span style={{ fontSize: 14 }}>
                    {currencyFormatter.format(i.value)}
                  </span>
                  {TabActive.BUDGET !== tabActive && savingsItem && (
                    <Box mt={0.5}>
                      <span
                        style={{
                          display: "inline-block",
                          fontSize: 12,
                          color: savingsItem.value > 0 ? "#D32F2F" : "#28C76F",
                          backgroundColor: "white",
                          padding: "2px 6px",
                          borderRadius: 4,
                        }}
                      >
                        {savingsItem.value > 0 ? "+" : ""}
                        {currencyFormatter.format(savingsItem.value)}
                      </span>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          <Box display="flex" alignItems="center" gap={1.5} mr={1.5}>
            <Tooltip title="Descritivos">
              <span style={{ display: "flex" }}>
                <IconButton
                  onClick={() => setDescriptionModalOpen(true)}
                  disabled={isBudgetDisabled}
                  size="small"
                  sx={{
                    color: "#1361A4",
                    background: "#FFF",
                    p: 0.5,
                    "&:hover": {
                      backgroundColor: "#F5F5F5",
                    },
                  }}
                >
                  <DescriptionBlueIcon width={16} height={16} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Remover fornecedor">
              <span style={{ display: "flex" }}>
                <IconButton
                  onClick={removeSupplier}
                  disabled={isBudgetDisabled}
                  size="small"
                  sx={{
                    color: "#FFF",
                    backgroundColor: "#D32F2F",
                    p: 0.25,
                    "&:hover": {
                      backgroundColor: "#B71C1C",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      <div style={{ padding: "15px 15px 2px 15px" }}>
        <Box mt={1} width={"100%"}>
          {renderActiveOptions()}
        </Box>
      </div>

      {/* <Box mt={1} width={"100%"}>
        {renderActiveOptions()}
      </Box> */}

      <Box display={"flex"} justifyContent={"flex-end"} px={1} py={0} gap={1}>
        <ActionSelect
          label={
            <>
              <AddIcon sx={{ fontSize: 20 }} />
              <span style={{ fontSize: 15 }}>Categoria</span>
            </>
          }
          actionOptions={generateFilteredOptions(
            BudgetItemTypeEnum,
            itemTypeList?.[activeCalcOption]
          )}
          triggerAction={addOption}
          disabled={isBudgetDisabled}
          applyTabActionFilters={false}
          fullWidth
          centerLabel
          hideDropdownIcon
          filled
        />
      </Box>
      <DescriptionModal
        isOpen={descriptionModalOpen}
        onClose={() => setDescriptionModalOpen(false)}
        descriptions={descriptions}
        onChange={handleDescriptionChange}
        onConfirm={handleDescriptionConfirm}
        hideComposition
      />
      {moveModalType && (
        <MoveCategoryBillingModal
          open
          onClose={() => setMoveModalType(null)}
          categoryLabel={
            BudgetItemTypeEnum[moveModalType as keyof typeof BudgetItemTypeEnum]
          }
          supplierLabel={provider?.name || "Fornecedor sem nome"}
          currentCalcOption={activeCalcOption as CalcOption}
          onConfirm={(target) => confirmMoveCategory(moveModalType, target)}
        />
      )}
      {transferModalType && (
        <TransferCategorySupplierModal
          open
          onClose={() => setTransferModalType(null)}
          categoryLabel={
            BudgetItemTypeEnum[
              transferModalType as keyof typeof BudgetItemTypeEnum
            ]
          }
          currentSupplierLabel={provider?.name || "Fornecedor sem nome"}
          currentSupplierRealId={parsedSupplierId}
          calcOption={activeCalcOption as CalcOption}
          existingSuppliers={transferTargets}
          onConfirm={(target) =>
            confirmTransferCategory(transferModalType, target)
          }
        />
      )}
    </Box>
  );
};

// `RenderBudgetInfo` rebuilds the supplier list on every parent render. The
// row props are primitive (supplierId / index / calcOption), so React.memo's
// default shallow compare is enough to skip rows that didn't move while the
// budget reducer is busy with edits in another row. The slice subscriptions
// inside the component still wake the row up correctly when its own data
// (supplierList / addedItemTypeList / providers map / totals) changes.
export default React.memo(BudgetSupplier);
